import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import "./PreviousPayrollPage.css";

const EditPreviousPayrollPage = () => {
  const { id: businessId } = useParams();
  const [payrollData, setPayrollData] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPayrollData = async () => {
      try {
        const { data } = await api.get(`/business/${businessId}/edit-previous-payroll`);

        // Group payroll data by employee_id
        const groupedData = data.payrollData.reduce((acc, record) => {
          if (!acc[record.employee_id]) {
            acc[record.employee_id] = {
              employee_id: record.employee_id, // ✅ keep employee_id
              employee_name: record.employee_name,
              payrolls: []
            };
          }
          acc[record.employee_id].payrolls.push(record);
          return acc;
        }, {});

        setPayrollData(Object.values(groupedData));
      } catch (error) {
        console.error("Error fetching payroll data:", error);
        alert("Failed to fetch payroll data.");
      }
    };

    fetchPayrollData();
  }, [businessId]);

  // ✅ delete handler
  const handleDeleteRecord = async (payrollId) => {
    const ok = window.confirm("Are you sure you want to delete this payroll record?");
    if (!ok) return;

    try {
      await api.delete(`/business/${businessId}/edit-previous-payroll/${payrollId}`);

      // ✅ remove deleted record from UI
      setPayrollData((prev) => {
        const updated = prev
          .map((emp) => ({
            ...emp,
            payrolls: emp.payrolls.filter((p) => p.id !== payrollId),
          }))
          .filter((emp) => emp.payrolls.length > 0); // remove empty employee cards
        return updated;
      });
    } catch (error) {
      console.error("Error deleting payroll record:", error);
      alert("Failed to delete payroll record.");
    }
  };

  return (
    <div className="payroll-container-previous">
      <h2 className="page-title-previous">View Past Payroll Data</h2>

      {payrollData.length > 0 ? (
        payrollData.map((employee) => (
          <div key={employee.employee_id} className="employee-card">
            <div className="employee-name">{employee.employee_name}</div>

            <table className="payroll-table-previous">
              <thead>
                <tr>
                  <th>Pay Period</th>
                  {employee.payrolls.some((p) => p.grosspay) && <th>Gross Pay</th>}
                  {employee.payrolls.some((p) => p.fed_tax) && <th>Federal Tax</th>}

                  {employee.payrolls.some((p) => p.state_tax) && (
                    <th>
                      {employee.payrolls[0]?.state
                        ? `${employee.payrolls[0].state} Tax`
                        : "State Tax"}
                    </th>
                  )}

                  {employee.payrolls.some((p) => p.work_state_tax) && (
                    <th>
                      {employee.payrolls[0]?.business_state
                        ? `${employee.payrolls[0].business_state} Tax`
                        : "State Tax"}
                    </th>
                  )}

                  {employee.payrolls.some((p) => p.resident_eit) && (
                    <th>
                      {employee.payrolls[0]?.local_city2
                        ? `${employee.payrolls[0].local_city2} EIT`
                        : "EIT"}
                    </th>
                  )}

                  {employee.payrolls.some((p) => p.non_resident_eit) && (
                    <th>
                      {employee.payrolls[0]?.local_city1
                        ? `${employee.payrolls[0].local_city1} EIT`
                        : "EIT"}
                    </th>
                  )}

                  {employee.payrolls.some((p) => p.local_citytax1) && (
                    <th>
                      {employee.payrolls[0]?.local_city1
                        ? `${employee.payrolls[0].local_city1} Tax`
                        : "City Tax"}
                    </th>
                  )}

                  {employee.payrolls.some((p) => p.local_citytax2) && (
                    <th>
                      {employee.payrolls[0]?.local_city2
                        ? `${employee.payrolls[0].local_city2} Tax`
                        : "City Tax"}
                    </th>
                  )}

                  {employee.payrolls.some((p) => p.lst_tax) && (
                    <th>
                      {employee.payrolls[0]?.lst_city
                        ? `${employee.payrolls[0].lst_city} LST`
                        : "LST Tax"}
                    </th>
                  )}

                  {employee.payrolls.some((p) => p.county_resident) && (
                    <th>
                      {employee.payrolls[0]?.e_county
                        ? `${employee.payrolls[0].e_county} Tax`
                        : "County Tax"}
                    </th>
                  )}

                  {employee.payrolls.some((p) => p.county_non_resident) && (
                    <th>
                      {employee.payrolls[0]?.b_county
                        ? `${employee.payrolls[0].b_county} Tax`
                        : "County Tax"}
                    </th>
                  )}

                  {/* ✅ actions column */}
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {employee.payrolls.map((payroll) => (
                  <tr key={payroll.id}>
                    <td style={{ fontWeight: "bold" }}>
                      {new Date(payroll.start_date).toISOString().split("T")[0]} -{" "}
                      {new Date(payroll.end_date).toISOString().split("T")[0]}
                    </td>

                    {payroll.grosspay && <td>{payroll.grosspay}</td>}
                    {payroll.fed_tax && <td>{payroll.fed_tax}</td>}
                    {payroll.state_tax && <td>{payroll.state_tax}</td>}
                    {payroll.work_state_tax && <td>{payroll.work_state_tax}</td>}
                    {payroll.resident_eit && <td>{payroll.resident_eit}</td>}
                    {payroll.non_resident_eit && <td>{payroll.non_resident_eit}</td>}
                    {payroll.local_citytax1 && <td>{payroll.local_citytax1}</td>}
                    {payroll.local_citytax2 && <td>{payroll.local_citytax2}</td>}
                    {payroll.lst_tax && <td>{payroll.lst_tax}</td>}
                    {payroll.county_resident && <td>{payroll.county_resident}</td>}
                    {payroll.county_non_resident && <td>{payroll.county_non_resident}</td>}

                    {/* ✅ delete button per record */}
                    <td>
                      <button
                        type="button"
                        className="delete-button-previous"
                        onClick={() => handleDeleteRecord(payroll.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      ) : (
        <p>No previous payroll data available.</p>
      )}

      <button
        className="cancel-button"
        onClick={() => navigate(`/dashboard/business/${businessId}`)}
      >
        Back to Dashboard
      </button>
    </div>
  );
};

export default EditPreviousPayrollPage;



// import React, { useEffect, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import api from "../services/api";
// import "./PreviousPayrollPage.css"; // Ensure you have this CSS file for styling

// const EditPreviousPayrollPage = () => {
//     const { id: businessId } = useParams();
//     const [payrollData, setPayrollData] = useState([]);
//     const navigate = useNavigate();

//     useEffect(() => {
//         const fetchPayrollData = async () => {
//             try {
//                 const { data } = await api.get(`/business/${businessId}/edit-previous-payroll`);
                
//                 // Group payroll data by employee_id
//                 const groupedData = data.payrollData.reduce((acc, record) => {
//                     if (!acc[record.employee_id]) {
//                         acc[record.employee_id] = {
//                             employee_name: record.employee_name,
//                             payrolls: []
//                         };
//                     }
//                     acc[record.employee_id].payrolls.push(record);
//                     return acc;
//                 }, {});

//                 setPayrollData(Object.values(groupedData)); // Convert object back to array
//             } catch (error) {
//                 console.error("Error fetching payroll data:", error);
//                 alert("Failed to fetch payroll data.");
//             }
//         };

//         fetchPayrollData();
//     }, [businessId]);

//     return (
//         <div className="payroll-container-previous">
//             <h2 className="page-title-previous">View Past Payroll Data</h2>
//             {payrollData.length > 0 ? (
//                 payrollData.map((employee) => (
//                     <div key={employee.employee_name} className="employee-card">
//                         <div className="employee-name">{employee.employee_name}</div>
//                         <table className="payroll-table-previous">
//                             <thead>
//                                 <tr>
//                                     <th>Pay Period</th>
//                                     {employee.payrolls.some(p => p.grosspay) && <th>Gross Pay</th>}
//                                     {employee.payrolls.some(p => p.fed_tax) && <th>Federal Tax</th>}
//                                     {employee.payrolls.some(p => p.state_tax) && (
//                                     <th>{employee.payrolls[0]?.state ? `${employee.payrolls[0].state} Tax` : "State Tax"}</th>
//                                 )}
                                    
//                                     {employee.payrolls.some(p => p.work_state_tax) && (
//                                     <th>{employee.payrolls[0]?.business_state ? `${employee.payrolls[0].business_state} Tax` : "State Tax"}</th>
//                                 )}
                            
//                                     {employee.payrolls.some(p => p.resident_eit) && (
//                                     <th>{employee.payrolls[0]?.local_city2 ? `${employee.payrolls[0].local_city2} EIT` : "EIT"}</th>
//                                 )}                            

//                                     {employee.payrolls.some(p => p.non_resident_eit) && (
//                                     <th>{employee.payrolls[0]?.local_city1 ? `${employee.payrolls[0].local_city1} EIT` : "EIT"}</th>
//                                 )}


//                                     {employee.payrolls.some(p => p.local_citytax1) && (
//                                     <th>{employee.payrolls[0]?.local_city1 ? `${employee.payrolls[0].local_city1} Tax` : "City Tax"}</th>
//                                 )}
//                                     {employee.payrolls.some(p => p.local_citytax2) && (
//                                     <th>{employee.payrolls[0]?.local_city2 ? `${employee.payrolls[0].local_city2} Tax` : "City Tax"}</th>
//                                 )}
                                    
//                                     {employee.payrolls.some(p => p.lst_tax) && (
//                                     <th>{employee.payrolls[0]?.lst_city ? `${employee.payrolls[0].lst_city} LST` : "LST Tax"}</th>
//                                 )}
                                    
                                   
//                                     {employee.payrolls.some(p => p.county_resident) && (
//                                     <th>{employee.payrolls[0]?.e_county ? `${employee.payrolls[0].e_county} Tax` : "County Tax"}</th>
//                                 )}
                                    
//                                     {employee.payrolls.some(p => p.county_non_resident) && (
//                                     <th>{employee.payrolls[0]?.b_county ? `${employee.payrolls[0].b_county} Tax` : "County Tax"}</th>
//                                 )}
//                                 </tr>
//                             </thead>
//                             <tbody>
//                                 {employee.payrolls.map((payroll, index) => (
//                                     <tr key={index}>
//                                         <td style={{fontWeight:'bold'}}>{new Date(payroll.start_date).toISOString().split("T")[0]} - {new Date(payroll.end_date).toISOString().split("T")[0]}</td>
//                                         {payroll.grosspay && <td>{payroll.grosspay}</td>}
//                                         {payroll.fed_tax && <td>{payroll.fed_tax}</td>}
//                                         {payroll.state_tax && <td>{payroll.state_tax}</td>}
//                                         {payroll.work_state_tax && <td>{payroll.work_state_tax}</td>}
//                                         {payroll.resident_eit && <td>{payroll.resident_eit}</td>}
//                                         {payroll.non_resident_eit && <td>{payroll.non_resident_eit}</td>}
//                                         {payroll.local_citytax1 && <td>{payroll.local_citytax1}</td>}
//                                         {payroll.local_citytax2 && <td>{payroll.local_citytax2}</td>}
//                                         {payroll.lst_tax && <td>{payroll.lst_tax}</td>}
//                                         {payroll.county_resident && <td>{payroll.county_resident}</td>}
//                                         {payroll.county_non_resident && <td>{payroll.county_non_resident}</td>}
//                                     </tr>
//                                 ))}
//                             </tbody>
//                         </table>
//                     </div>
//                 ))
//             ) : (
//                 <p>No previous payroll data available.</p>
//             )}
//             <button className="cancel-button" onClick={() => navigate(`/dashboard/business/${businessId}`)}>
//                 Back to Dashboard
//             </button>
//         </div>
//     );
// };

// export default EditPreviousPayrollPage;




// import React, { useEffect, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import api from "../services/api";
// import "./PreviousPayrollPage.css"; // Ensure you have this CSS file for styling

// const EditPreviousPayrollPage = () => {
//     const { id: businessId } = useParams();
//     const [payrollData, setPayrollData] = useState([]);
//     const navigate = useNavigate();

//     useEffect(() => {
//         const fetchPayrollData = async () => {
//             try {
//                 const { data } = await api.get(`/business/${businessId}/edit-previous-payroll`);
                
//                 // Group payroll data by employee_id
//                 const groupedData = data.payrollData.reduce((acc, record) => {
//                     if (!acc[record.employee_id]) {
//                         acc[record.employee_id] = {
//                             employee_name: record.employee_name,
//                             payrolls: []
//                         };
//                     }
//                     acc[record.employee_id].payrolls.push(record);
//                     return acc;
//                 }, {});

//                 setPayrollData(Object.values(groupedData)); // Convert object back to array
//             } catch (error) {
//                 console.error("Error fetching payroll data:", error);
//                 alert("Failed to fetch payroll data.");
//             }
//         };

//         fetchPayrollData();
//     }, [businessId]);

//     return (
//         <div className="payroll-container">
//             <h2 className="page-title">View Past Payroll Data</h2>
//             {payrollData.length > 0 ? (
//                 payrollData.map((employee) => (
//                     <div key={employee.employee_name} className="employee-card">
//                         <div className="employee-name">{employee.employee_name}</div>
//                         <table className="payroll-table">
//                             <thead>
//                                 <tr>
//                                     <th>Pay Period</th>
//                                     <th>Gross Pay</th>
//                                     <th>Federal Tax</th>
//                                     <th>State Tax</th>
//                                     <th>Business State Tax</th>
//                                     <th>Resident EIT</th>
//                                     <th>Non-Resident EIT</th>
//                                     <th>Local Tax 1</th>
//                                     <th>Local Tax 2</th>
//                                     <th>LST Tax</th>
//                                     <th>County Tax (Resident)</th>
//                                     <th>County Tax (Non-Resident)</th>
//                                 </tr>
//                             </thead>
//                             <tbody>
//                                 {employee.payrolls.map((payroll, index) => (
//                                     <tr key={index}>
//                                         <td>{new Date(payroll.start_date).toISOString().split("T")[0]} - {new Date(payroll.end_date).toISOString().split("T")[0]}</td>
//                                         <td>{payroll.grosspay || "N/A"}</td>
//                                         <td>{payroll.fed_tax || "N/A"}</td>
//                                         <td>{payroll.state_tax ? `${payroll.state} Tax: ${payroll.state_tax}` : "N/A"}</td>
//                                         <td>{payroll.work_state_tax ? `${payroll.business_state} Tax: ${payroll.work_state_tax}` : "N/A"}</td>
//                                         <td>{payroll.resident_eit ? `${payroll.state} Resident EIT: ${payroll.resident_eit}` : "N/A"}</td>
//                                         <td>{payroll.non_resident_eit ? `${payroll.business_state} Non-Resident EIT: ${payroll.non_resident_eit}` : "N/A"}</td>
//                                         <td>{payroll.local_citytax1 ? `${payroll.local_city1} Tax: ${payroll.local_citytax1}` : "N/A"}</td>
//                                         <td>{payroll.local_citytax2 ? `${payroll.local_city2} Tax: ${payroll.local_citytax2}` : "N/A"}</td>
//                                         <td>{payroll.lst_tax ? `${payroll.lst_city} LST Tax: ${payroll.lst_tax}` : "N/A"}</td>
//                                         <td>{payroll.county_resident ? `${payroll.e_county} Tax: ${payroll.county_resident}` : "N/A"}</td>
//                                         <td>{payroll.county_non_resident ? `${payroll.b_county} Tax: ${payroll.county_non_resident}` : "N/A"}</td>
//                                     </tr>
//                                 ))}
//                             </tbody>
//                         </table>
//                     </div>
//                 ))
//             ) : (
//                 <p>No previous payroll data available.</p>
//             )}
//             <button className="cancel-button" onClick={() => navigate(`/dashboard/business/${businessId}`)}>
//                 Back to Dashboard
//             </button>
//         </div>
//     );
// };

// export default EditPreviousPayrollPage;




// import React, { useEffect, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import api from "../services/api";
// import "./PreviousPayrollPage.css"; // Ensure this file is properly styled

// const EditPreviousPayrollPage = () => {
//     const { id: businessId } = useParams();
//     const [payrollData, setPayrollData] = useState([]);
//     const navigate = useNavigate();

//     useEffect(() => {
//         const fetchPayrollData = async () => {
//             try {
//                 const { data } = await api.get(`/business/${businessId}/edit-previous-payroll`);
//                 setPayrollData(data.payrollData);
//             } catch (error) {
//                 console.error("Error fetching payroll data:", error);
//                 alert("Failed to fetch payroll data.");
//             }
//         };

//         fetchPayrollData();
//     }, [businessId]);

//     return (
//         <div className="payroll-container">
//             <h2 className="page-title">View Past Payroll Data</h2>
//             {payrollData.length > 0 ? (
//                 payrollData.map((employee) => {
//                     return (
//                         <div key={employee.employee_id} className="employee-card">
//                             <div className="employee-name">{employee.employee_name}</div>
//                             <table className="payroll-table">
//                                 <tbody>
//                                     <tr>
//                                         <td><strong>Pay Period:</strong></td>
//                                         <td>{new Date(employee.start_date).toISOString().split("T")[0]} - {new Date(employee.end_date).toISOString().split("T")[0]}</td>
//                                     </tr>
//                                     {employee.grosspay && (
//                                         <tr>
//                                             <td><strong>Gross Pay:</strong></td>
//                                             <td>{employee.grosspay}</td>
//                                         </tr>
//                                     )}
//                                     {employee.fed_tax && (
//                                         <tr>
//                                             <td><strong>Federal Tax:</strong></td>
//                                             <td>{employee.fed_tax}</td>
//                                         </tr>
//                                     )}
//                                     {employee.state_tax && (
//                                         <tr>
//                                             <td><strong>{employee.state} Tax:</strong></td>
//                                             <td>{employee.state_tax}</td>
//                                         </tr>
//                                     )}
//                                     {employee.work_state_tax && (
//                                         <tr>
//                                             <td><strong>{employee.business_state} Tax:</strong></td>
//                                             <td>{employee.work_state_tax}</td>
//                                         </tr>
//                                     )}
//                                     {employee.resident_eit && (
//                                         <tr>
//                                             <td><strong>{employee.state} Resident EIT:</strong></td>
//                                             <td>{employee.resident_eit}</td>
//                                         </tr>
//                                     )}
//                                     {employee.non_resident_eit && (
//                                         <tr>
//                                             <td><strong>{employee.business_state} Non-Resident EIT:</strong></td>
//                                             <td>{employee.non_resident_eit}</td>
//                                         </tr>
//                                     )}
//                                     {employee.local_citytax1 && (
//                                         <tr>
//                                             <td><strong>{employee.local_city1} Tax:</strong></td>
//                                             <td>{employee.local_citytax1}</td>
//                                         </tr>
//                                     )}
//                                     {employee.local_citytax2 && (
//                                         <tr>
//                                             <td><strong>{employee.local_city2} Tax:</strong></td>
//                                             <td>{employee.local_citytax2}</td>
//                                         </tr>
//                                     )}
//                                     {employee.lst_tax && (
//                                         <tr>
//                                             <td><strong>{employee.lst_city} LST Tax:</strong></td>
//                                             <td>{employee.lst_tax}</td>
//                                         </tr>
//                                     )}
//                                     {employee.county_resident && (
//                                         <tr>
//                                             <td><strong>{employee.e_county} Tax:</strong></td>
//                                             <td>{employee.county_resident}</td>
//                                         </tr>
//                                     )}
//                                     {employee.county_non_resident && (
//                                         <tr>
//                                             <td><strong>{employee.b_county} Tax:</strong></td>
//                                             <td>{employee.county_non_resident}</td>
//                                         </tr>
//                                     )}
//                                 </tbody>
//                             </table>
//                         </div>
//                     );
//                 })
//             ) : (
//                 <p>No previous payroll data available.</p>
//             )}
//             <button className="cancel-button" onClick={() => navigate(`/dashboard/business/${businessId}`)}>
//                 Back to Dashboard
//             </button>
//         </div>
//     );
// };

// export default EditPreviousPayrollPage;




// import React, { useEffect, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import api from "../services/api";
// import "./PreviousPayrollPage.css"; // Ensure you have this CSS file for styling

// const EditPreviousPayrollPage = () => {
//     const { id: businessId } = useParams();
//     const [payrollData, setPayrollData] = useState([]);
//     const navigate = useNavigate();

//     useEffect(() => {
//         const fetchPayrollData = async () => {
//             try {
//                 const { data } = await api.get(`/business/${businessId}/edit-previous-payroll`);
//                 setPayrollData(data.payrollData);
//             } catch (error) {
//                 console.error("Error fetching payroll data:", error);
//                 alert("Failed to fetch payroll data.");
//             }
//         };

//         fetchPayrollData();
//     }, [businessId]);

//     // Helper function to filter out "N/A" values
//     const getFilteredData = (employee) => {
//         const entries = [
//             { label: "Gross Pay", value: employee.grosspay },
//             { label: "Federal Tax", value: employee.fed_tax },
//             { label: `${employee.state} Tax`, value: employee.state_tax },
//             { label: `${employee.business_state} Tax`, value: employee.work_state_tax },
//             { label: `${employee.state} Resident EIT`, value: employee.resident_eit },
//             { label: `${employee.business_state} Non-Resident EIT`, value: employee.non_resident_eit },
//             { label: `${employee.local_city1} Tax`, value: employee.local_citytax1 },
//             { label: `${employee.local_city2} Tax`, value: employee.local_citytax2 },
//             { label: `${employee.lst_city} LST Tax`, value: employee.lst_tax },
//             { label: `${employee.e_county} Tax`, value: employee.county_resident },
//             { label: `${employee.b_county} Tax`, value: employee.county_non_resident },
//         ];
        
//         // Remove fields where value is falsy (null, undefined, 0, or empty)
//         return entries.filter(entry => entry.value);
//     };

//     return (
//         <div className="payroll-container">
//         <h2 className="page-title">View Past Payroll Data</h2>
    
//         {payrollData.length > 0 ? (
//             <div className="payroll-wrapper">
//                 {payrollData.map((employee) => {
//                     const filteredEntries = getFilteredData(employee);
//                     if (filteredEntries.length === 0) return null; // Skip if no data available
    
//                     return (
//                         <div key={employee.employee_id} className="payroll-card">
//                             <h3>{employee.employee_name}</h3>
//                             <p><strong>Pay Period:</strong> {new Date(employee.start_date).toISOString().split("T")[0]} - {new Date(employee.end_date).toISOString().split("T")[0]}</p>
    
//                             {filteredEntries.map((entry, index) => (
//                                 <p key={index}><strong>{entry.label}:</strong> {entry.value}</p>
//                             ))}
//                         </div>
//                     );
//                 })}
//             </div>
//         ) : (
//             <p>No previous payroll data available.</p>
//         )}
    
//         <button className="cancel-button" onClick={() => navigate(`/dashboard/business/${businessId}`)}>
//             Back to Dashboard
//         </button>
//     </div>
    
//     );
// };

// export default EditPreviousPayrollPage;




// import React, { useEffect, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import api from "../services/api";
// import "./PreviousPayrollPage.css"; // Ensure you have this CSS file for styling

// const EditPreviousPayrollPage = () => {
//     const { id: businessId } = useParams();
//     const [payrollData, setPayrollData] = useState([]);
//     const navigate = useNavigate();

//     useEffect(() => {
//         const fetchPayrollData = async () => {
//             try {
//                 const { data } = await api.get(`/business/${businessId}/edit-previous-payroll`);
//                 setPayrollData(data.payrollData); // Store payroll data in state
//             } catch (error) {
//                 console.error("Error fetching payroll data:", error);
//                 alert("Failed to fetch payroll data.");
//             }
//         };

//         fetchPayrollData();
//     }, [businessId]);

//     return (
//         <div className="payroll-container">
//             <h2 className="page-title">View Past Payroll Data</h2>
//             {payrollData.length > 0 ? (
//                 <table className="payroll-table">
//                     <tbody>
//                         {payrollData.map((employee) => (
//                             <tr key={employee.employee_id}>
//                                 <td>{new Date(employee.start_date).toISOString().split("T")[0]} to {new Date(employee.end_date).toISOString().split("T")[0]}</td>
//                                 <td>Name: {employee.employee_name}</td>
//                                 <td> Gross Pay{employee.grosspay}</td>
//                                 <td> Federal Tax{employee.fed_tax}</td>
                                
//                                 {/* State Taxes */}
                        
//                                 <td>{employee.state_tax ? `${employee.state} Tax: ${employee.state_tax}` : "N/A"}</td>

//                                 {/* Business State Taxes */}
                            
//                                 <td>{employee.work_state_tax ? `${employee.business_state} Tax: ${employee.work_state_tax}` : "N/A"}</td>

//                                 {/* EIT Taxes */}
//                                 <td>{employee.resident_eit ? `${employee.state} Resident EIT: ${employee.resident_eit}` : "N/A"}</td>
//                                 <td>{employee.non_resident_eit ? `${employee.business_state} Non-Resident EIT: ${employee.non_resident_eit}` : "N/A"}</td>

//                                 {/* Local City Taxes */}
                                
//                                 <td>{employee.local_citytax1 ? `${employee.local_city1} Tax: ${employee.local_citytax1}` : "N/A"}</td>
                               
//                                 <td>{employee.local_citytax2 ? `${employee.local_city2} Tax: ${employee.local_citytax2}` : "N/A"}</td>

//                                 {/* LST Taxes */}
                                
//                                 <td>{employee.lst_tax ? `${employee.lst_city} LST Tax: ${employee.lst_tax}` : "N/A"}</td>

//                                 {/* County Taxes */}
                                
//                                 <td>{employee.county_resident ? `${employee.e_county} Tax: ${employee.county_resident}` : "N/A"}</td>
                               
//                                 <td>{employee.county_non_resident ? `${employee.b_county} Tax: ${employee.county_non_resident}` : "N/A"}</td>

                                
//                             </tr>
//                         ))}
//                     </tbody>
//                 </table>
//             ) : (
//                 <p>No previous payroll data available.</p>
//             )}
//             <button
//                 className="cancel-button"
//                 onClick={() => navigate(`/dashboard/business/${businessId}`)}
//             >
//                 Back to Dashboard
//             </button>
//         </div>
//     );
// };

// export default EditPreviousPayrollPage;
